import { createRef } from 'react'
import create from 'zustand'
import { UseNotesApi } from '../components/common/notes'

import { partition } from './arrays'
import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit'
import { CollectionChangeApi, collectionChanges } from './change-api'
import {
  autoCompleteRequest,
  findDxOrProcedureRequestList,
  findDxOrProcedures,
  loadCptRequest,
  loadCritCareCpts,
} from './codesets'
import { dateFromInput, utcDateString } from './date-time'
import {
  addIcdToAllCpts,
  addOneIcdToEmProcedures,
  diagnosis,
  setAllDiagnosesOnEmProcedures,
  setProcedureDiagnoses,
  updateIcdLineNumbers,
} from './diagnosis'

import { documentBundleLabel, documentResolver } from './document-resolver'
import { DocumentRef } from './document-viewer/document-types'
import {
  Audit,
  CodeSet,
  CriticalCareCode,
  Diagnosis,
  emElements,
  EmResult,
  Procedure,
  Provider,
  validatePlaceOfService,
  Worksheet,
  WorksheetComponents,
} from './domain'
import { EmCalculatorStore } from './em-calculator-api'
import { formApi, FormApi, providerField } from './form'
import { KeyboardApi, keyboardEvents } from './keyboard'
import { inactiveListApi, listApi, ListApi, MoveItem } from './list'
import {
  cptEquals,
  cptId,
  CptPatchMap,
  cptPatchMap,
  hasBeenBilled,
  modifierLinkString,
  postProcessCpts,
  procedure,
  removeDxFromCptList,
  setProcedureLinksFromDiagnosesPosition,
  updateCriticalCareV2,
} from './procedure'

import { worksheetAlertUpdate } from './alerts/alerts-api'
import { showSuccessMessage, showToast } from './toast'
import { filterForCodeset, truCode, TruCodePost } from './trucode'
import { ValueApi, valueApi } from './value'
import { BaseCode, WorksheetContext } from './visit-context'
import { currentWorkflow, findFeature, isAlertsV2, isReviewWorkflowState } from './workflow-api'
import { WorksheetFormApi } from './worksheet-form-api'
import { createdByCurrentUser, setDiagnosisTransients, setProcedureTransients, WorksheetElement } from './worksheets'
import { accountValidation } from './document-viewer/document-api'
import { log } from './log'
import { NdcApi } from './ndc'

type Code = Procedure | Diagnosis
interface CodeEntry {
  code: any
  modifier: string
  presentOnAdmission: string
  multiplier: number
  links: string
  serviceDate: Date
  provider: Provider
  secondary: Provider
  tertiary: Provider
  textSearch?: boolean
  placeOfService?: any
}
export interface IcdCptApi extends WorksheetFormApi<CodeEntry, Code> {
  worksheet: Worksheet

  icds: ListApi<Diagnosis>
  inactiveIcds: ListApi<Diagnosis>
  icdChanges: CollectionChangeApi<Diagnosis>
  cpts: ListApi<Procedure>
  inactiveCpts: ListApi<Procedure>
  billedCpts?: ListApi<Procedure>
  cptChanges: CollectionChangeApi<Procedure>
  notesApi: UseNotesApi
  ndc: NdcApi
  changed: ValueApi<boolean>
  context?: WorksheetContext
  doc?: DocumentRef
  calculator?: EmCalculatorStore
  isReview?: boolean
  worksheetReplacement?: boolean

  moveIcd(item: MoveItem): void
  addIcd(code: CodeSet): void
  removeIcd(dx: Diagnosis): void
  editIcd(icd: Diagnosis): void
  removeCpt(cpt: Procedure): void
  editCpt(cpt: Procedure, setEdit?: boolean, onCancel?: () => void): void
  cancelEdit(): void
  addCpt(
    code: CodeSet,
    add: boolean,
    review?: boolean,
    override?: Partial<Procedure>,
    onComplete?: (a: Procedure) => void,
  ): Procedure
  moveCpt(item: MoveItem): void
  setCode(code: string): void
  findCode(post: TruCodePost): void
  current(): Worksheet
  validate(onComplete?: (hasAlerts?: boolean) => void): void
  getValidated: () => boolean
  getFullText: () => boolean
  destroy(): void
  setChanged(): void
  setWorksheetReplacement(enabled: boolean): void
  setReasonForVisit(a: Diagnosis[]): void
  setEmCalculator(cpt: Procedure): void
  onEditEnd(cpt: Procedure): void
  resetTopForm(): void
  onFullTextChanged(fn: (a: boolean) => void): void
  appendInactive(a: Worksheet): Worksheet
  reviewEdit(update: Procedure, original: Procedure, onComplete: () => void, onCancel?: () => void): void
}

export function icdCptApi(context: WorksheetContext): IcdCptApi {
  const { worksheet, status, config, notesApi, alerts, ndc, readonly, calculator } = context
  const { owningProvider, locationCode } = worksheet
  const { providers, primaryProviders, secondaryProviders, tertiaryProviders, emLinks, cptLinks } = {
    ...{ emLinks: true, cptLinks: true },
    ...config,
  }

  let lastCodeSetForTruCode: CodeSet = null
  let pendingSearch: boolean

  const changed = valueApi<boolean>({ defaultValue: false })
  const setChanged = () => {
    changed.getState().setValue(!changed.getState().value)
  }

  const isReview = isReviewWorkflowState()
  const defaultFilter = isReview ? 'active' : 'active'

  const icds = listApi({
    defaultFilter,
    all: worksheet.diagnoses,
    id: (a) => a.diagnosisCode,
    equals: (a, b) => a.diagnosisCode === b.diagnosisCode,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
    destructive: !isReview,
    allowDuplicates: false,
    postProcess: (v) => updateIcdLineNumbers(v),
  })

  function updateCptLinks() {
    const newIcds = icds.actions.values()
    let newCpts = cpts.actions.all()
    newCpts = setProcedureLinksFromDiagnosesPosition(newCpts, newIcds)
    cpts.actions.setValuesDestructive(newCpts)
  }

  let icdMoved: Diagnosis[]
  let lastIcd: Diagnosis

  icds.events.values(() => {
    if (emLinks && lastIcd) {
      // Re-adding a diagnosis code that already exists in the collection (e.g. one that
      // was just removed) makes icds.actions.add() merge into a new object rather than
      // mutating `lastIcd` in place, so `lastIcd.lineNumber` can be stale/undefined here.
      // Look up the current stored diagnosis by code so the line number is always correct.
      const icd = icds.actions.values().find((v) => v.diagnosisCode === lastIcd.diagnosisCode) || lastIcd
      addOneIcdToEmProcedures(icd, cpts.actions.values())
      cpts.actions.refresh()
      lastIcd = null
    }
    if (icdMoved) {
      updateCptLinks()
      cpts.actions.refresh()
      icdMoved = null
    } else {
      cpts.actions.refresh()
    }
    setChanged()
  })

  truCode.getState().closeWindow()
  const lists = partition(worksheet.procedures, hasBeenBilled)

  const billedCpts = lists[0].length
    ? listApi({
        all: lists[0],
        id: (a) => a.cptCode,
        equals: (a, b) => a.cptCode === b.cptCode,
        active: (v) => v.active,
        setActive: (v) => (v.active = true),
        setInactive: (v) => (v.active = false),
      })
    : null

  const cpts = listApi({
    defaultFilter,
    all: lists[1],
    allowDuplicates: true,
    id: cptId,
    equals: cptEquals,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
    destructive: false,
    postProcess: postProcessCpts({
      autoAssignEm: emLinks,
      icdProvider: () => icds.data.getState().values,
    }),
  })

  cpts.events.values(() => setChanged())

  let bundle: { version: string | number }

  documentResolver.onDocumentBundleSelected((a: any) => {
    if (a.worksheetId && a.worksheetId === worksheet.worksheetId) {
      if (a.version !== bundle?.version) {
        showSuccessMessage(`Document Bundle Selected: ${documentBundleLabel(a)}`)
      }
      bundle = { version: a.version }
    }
  })

  const setStatus = status.getState().setStatus
  let validated = false

  function appendInactive(a: Worksheet) {
    const sheet = { ...a }
    const icd = inactiveIcds.actions.values()
    const cpt = inactiveCpts.actions.values()
    console.info('CODING activity, checking for inactive audit maps...', icd, cpt)
    if (!isReviewWorkflowState()) {
      sheet.diagnoses.push(...icd)
      sheet.procedures.push(...cpt)
    }
    return sheet
  }

  let documentAccount: { worksheetId: string; account: string }
  accountValidation.onmessage = (e) => {
    if (e.data) {
      documentAccount = e.data
    }
  }
  function validateAccount() {
    if (documentAccount) {
      if (documentAccount.worksheetId !== worksheet.worksheetId) {
        log.error('Document worksheet mismatch', worksheet.worksheetId, documentAccount.worksheetId)
      }
    }
  }

  const validate = (onComplete?: (hasAlerts?: boolean) => void) => {
    validated = true
    if (worksheetReplacement) {
      worksheetAlertUpdate(
        alerts,
        (error) => setStatus(error),
        onComplete,
        (a: WorksheetComponents) => {
          const { procedures, diagnoses } = a.worksheet

          validatePlaceOfService(procedures, getPlaceOfService())

          const updatedDx = (diagnoses || []).map((v) => setDiagnosisTransients(v, workflow))
          const updatedCpt = (procedures || []).map((v) => setProcedureTransients(v, updatedDx, workflow))
          icds.actions.setValuesDestructive(updatedDx)
          cpts.actions.setValuesDestructive(updatedCpt)
        },
      )(appendInactive(current()))
    }
  }

  const hasPlace = config?.placeOfService?.length > 0

  function isPoaFeatureEligible() {
    const poaFeatureConfig = findFeature('PresentOnAdmission')
    if (!!poaFeatureConfig?.enabled) {
      const poaWorksheetTypes = poaFeatureConfig?.parameters?.worksheetTypes || []
      return poaWorksheetTypes.includes(worksheet.worksheetType)
    }

    return false
  }

  const initialValues = {
    code: null,
    modifier: '',
    multiplier: 1,
    presentOnAdmission: '',
    links: null,
    serviceDate: dateFromInput(worksheet.serviceDate),
    provider: owningProvider,
    secondary: null,
    tertiary: null,
    textSearch: false,
    placeOfService: hasPlace ? config.placeOfService[0] : null,
  }

  let input = ''
  let fullText = false
  let fullTextChanged: (a: boolean) => void
  const form: FormApi<any> = formApi({
    disabled: readonly,
    values: initialValues,
    fields: {
      textSearch: {
        name: 'textSearch',
        id: 'textSearch',
        tabIndex: -1,
        style: { width: 40 },
        onChange: (a: boolean) => {
          fullText = a
          if (fullTextChanged) {
            fullTextChanged(a)
          }
        },
        onEnter: () => {
          textSearch.selected.setState({ value: !fullText })
          search(input)
        },
      },
      code: {
        label: 'Code',
        name: 'code',
        required: true,
        list: [],
        style: { width: '100%' },
        listLabel: (c: CodeSet) =>
          (c && c.description && c.code + ' - ' + c.codeType + ' - ' + c.description.longDescription) || '',
        textLabel: (c: CodeSet) => (c && c.code) || '',
        onEnter: () => form.submit(),
        inputRef: createRef(),
        onInputChange: (v: string) => {
          v = (v || '').replace(/`/g, ' ')

          if (!v.length && form?.controls?.code) {
            form.controls.code.list.getState().setValue([])
            return
          }
          input = v
          if (v.length >= 2) {
            if (v.indexOf(' ') > -1) {
              const filter = filterForCodeset(lastCodeSetForTruCode)
              // console.log('codeBook, filter', filter, lastCodeSet);
              console.info('TruCode search', v)
              truCode.getState().openCodeBooks({
                serviceDate: getSearchDate(),
                filter,
                query: v.replace(' ', '').trim(),
                events: {
                  onCodePosted: (e) => {
                    if (e.codes && e.codes.length) {
                      cancelEdits(false)
                      findCode(e.codes[0])
                    }
                  },
                },
              })
              setCode(null)
            } else {
              search(v)
            }
          }
        },
        onKeyUp: async (v: string) => {},
      },
      presentOnAdmission: {
        tooltip: 'Present on Admission',
        label: 'POA',
        list: config?.presentOnAdmission || [],
        textLabel: (c: CodeSet) => (c && c.code) || '',
        listLabel: (c: BaseCode) => (c && c.description && `${c.code} - ${c.description}`) || '',
        onEnter: () => form.submit(),
      },
      modifier: {
        label: 'Mod',
        name: 'modifier',
        style: { width: '100%' },
        onEnter: () => form.submit(),
      },
      multiplier: {
        label: 'Mult',
        name: 'multiplier',
        min: 1,
        max: 999,
        style: { width: '100%' },
        onEnter: () => form.submit(),
      },
      links: {
        label: 'Links',
        name: 'links',
        style: { width: '100%' },
        onEnter: () => form.submit(),
      },
      serviceDate: {
        required: true,
        label: 'Service Date',
        name: 'serviceDate',
        minDate: undefined,
        maxDate: new Date(),
        style: { width: '100%' },
        onEnter: () => form.submit(),
      },
      provider: providerField(primaryProviders, {
        onEnter: !context.secondaryEnabled && (() => form.submit()),
        optionDisabled: context.secondaryEnabled && uniqueProvider('primary'),
      }),
      secondary: providerField(secondaryProviders, {
        label: 'Secondary',
        required: false,
        onEnter: !context.tertiaryEnabled && (() => form.submit()),
        optionDisabled: uniqueProvider('secondary'),
        onChange: setTertiatryProvider,
      }),
      tertiary: providerField(tertiaryProviders, {
        label: 'Tertiary',
        required: false,
        optionDisabled: uniqueProvider('tertiary'),
        onEnter: () => form.submit(),
      }),
      placeOfService: {
        required: hasPlace,
        label: 'Service Location',
        list: config?.placeOfService || [],
        listLabel: (a: any) => a.description,
        onEnter: () => form.submit(),
      },
    },
    valid: (v) => inputValue() && v.serviceDate && v.provider !== null,
    onSubmit: (v) => findCode(v.code, true),
    resetValues: () => defaultValues(),
  })

  function defaultValues() {
    return {
      ...initialValues,
      ...{
        provider: provider.value.getState().value,
        secondary: secondary.value.getState().value,
        tertiary: tertiary.value.getState().value,
      },
    }
  }
  function cancelEdits(resetValues = true) {
    editingCpt = null
    editingIcd = null

    if (resetValues) {
      form.setValues(defaultValues(), true)
    }
    icds.actions.cancelEdit()
    cpts.actions.cancelEdit()
  }

  function getSearchDate() {
    const date: Date = serviceDate.value.getState().value || new Date()
    return utcDateString(date)
  }

  function setAutoComplete(r: CodeSet[]) {
    if (r && r.length) {
      lastCodeSetForTruCode = r[0]

      if (form.controls.code) {
        form.controls.code.list.getState().setValue(r)
      }
    }
  }

  const search = async (v: string) => {
    if (!pendingSearch && v && v.length >= 2) {
      pendingSearch = true

      let serviceDate = getSearchDate()
      const r: CodeSet[] = await autoCompleteRequest({
        serviceDate,
        fullText,
        code: v,
      })
      try {
        Array.isArray(r) ? setAutoComplete(r) : setAutoComplete([])
      } catch (e) {
        throw e
      } finally {
        pendingSearch = false
      }
    }
  }
  const findCode = async (c?: Partial<CodeSet>, clearInput = false) => {
    let v = inputValue(c, clearInput)

    setStatus(null)
    //TODO normalize description location in codeset methods (autocomplete returns description object, vs string here)

    const serviceDate = getSearchDate()

    const { locationCode, departmentCode, worksheetType } = worksheet
    let errors = ''

    const dx = editingIcd
    const cpt = editingCpt
    const codeType = c?.codeType || (editingIcd ? 'ICD_10' : undefined)

    const result = await findDxOrProcedures(
      {
        dx,
        cpt,
        serviceDate,
        codeType,
        locationCode,
        departmentCode,
        worksheetType,
        workflow,
        code: v,
      },
      (e) => (errors = e || 'No result found for ' + v),
    )

    const codes: CodeSet[] = (result || []).map((a: CodeSet) => ({
      ...a,
      ...{
        description: {
          longDescription: a.longDescription,
          shortDescription: a.shortDescription,
          description: a.longDescription,
        },
      },
    }))

    if (codes?.length > 0) {
      if (codes?.length === 1) {
        const codeSet = codes[0]

        if (codeSet.codeType === 'ICD_10') {
          if (isPoaFeatureEligible() && !form.values.getState().value?.presentOnAdmission) {
            setStatus('Please select the Present on Admission flag.')
            setTimeout(() => setCode(v, true), 10)
            return
          }

          addIcd(codeSet)
        } else {
          if (!form.values.getState().value.provider) {
            setStatus('Please select a provider.')
            setTimeout(() => setCode(v, true), 10)
            return
          }
          addCpt(codeSet, true, true, null, (cpt) => onEditEnd(cpt))
        }

        code.value.setState({ value: null })
        setCode('', false)
      } else {
        setStatus('Multiple results found for ' + v)
        const el = inputElement()
        if (el) {
          el.value = v
          el.blur()
          setTimeout(() => setCode(v, true), 10)
        }
      }
    } else {
      setStatus(errors)
    }
    input = ''
    setTimeout(() => setCode('', true), 10)
    form.setValues(defaultValues(), true)
    validated = false
  }

  const onIcdAdded = (dx: Diagnosis) => {
    addIcdToAllCpts(dx, {
      diagnoses: icds.actions.values(),
      procedures: cpts.actions.values(),
    })
    cpts.actions.refresh()
  }

  let worksheetReplacement = isAlertsV2(locationCode)
  if (worksheetReplacement) {
    icds.events.added(() => validate())
    icds.events.removed(() => validate())
    cpts.events.added(() => validate())
    cpts.events.removed(() => validate())
  }

  function setWorksheetReplacement(a: boolean) {
    worksheetReplacement = a
  }

  function addIcd(c: CodeSet) {
    validateAccount()
    const dx = diagnosis(c, workflow)

    dx.presentOnAdmission = form.values.getState().value?.presentOnAdmission?.code
    lastIcd = dx
    const match = (v: Diagnosis) => v.diagnosisCode === editingIcd.diagnosisCode

    function cleanUp() {
      editingIcd = null
      icds.data.setState({ edited: editingIcd })
    }

    if (editingIcd) {
      dx.diagnosisId = editingIcd.diagnosisId

      reviewChangesOr(
        {
          title: 'Revise ICD ' + dx.diagnosisCode,
          original: editingIcd,
          update: dx,
          changeReasons: {
            diagnosisCode: context.config.auditICD,
          },
          fieldNames: ['diagnosisCode'],
          onComplete: (v) => {
            v.action = 'UPDATE'
            updateElementAudit(dx, v)
            icds.actions.replace(dx, match)
            onIcdAdded(dx)
            cleanUp()
          },
          onCancel: () => {
            editingIcd = null
            icds.data.setState({ edited: editingIcd })
          },
        },
        () => {
          icds.actions.replace(dx, match)
          onIcdAdded(dx)
          cleanUp()
        },
      )
    } else {
      reviewActionOr(
        {
          title: 'Add ICD ' + dx.diagnosisCode,
          list: context.config.auditICD,
          onComplete: (v) => {
            v.action = 'CREATE'
            updateElementAudit(dx, v)
            icds.actions.add(dx)
            onIcdAdded(dx)
            cleanUp()
          },
          onCancel: () => (editingIcd = null),
        },
        () => {
          icds.actions.add(dx)
          onIcdAdded(dx)
          cleanUp()
        },
      )
    }
  }

  let editingIcd: Diagnosis = null
  function editIcd(v: Diagnosis): void {
    editingIcd = v
    icds.data.setState({ edited: editingIcd })
    setCode(v.diagnosisCode, true)

    api.onEdit()
    icds.actions.edit(v)
    form.setValues({
      code: { code: v.diagnosisCode },
      presentOnAdmission: presentOnAdmission.setListValue((u: BaseCode) => v.presentOnAdmission === u.code),
    })
  }

  const {
    code,
    provider,
    secondary,
    tertiary,
    serviceDate,
    multiplier,
    textSearch,
    placeOfService,
    presentOnAdmission,
  } = form.controls

  function setTertiatryProvider(value: Provider) {
    tertiary.value.setState({ value })
    const v = form.values.getState().value
    v.tertiary = value
  }

  function uniqueProvider(type: 'primary' | 'secondary' | 'tertiary') {
    return (o: Provider) => {
      const p = provider.value.getState().value
      const s = secondary.value.getState().value
      const t = tertiary.value.getState().value
      switch (type) {
        case 'primary':
          return o?.providerId === s?.providerId || o?.providerId === t?.providerId
        case 'secondary':
          return o?.providerId === p?.providerId
        case 'tertiary':
          return o?.providerId === p?.providerId
      }
    }
  }

  const workflow = currentWorkflow()

  function hardDelete(_: WorksheetElement): boolean {
    return (workflow || '').toLowerCase().includes('coding')
  }

  let reasonForVisit: Diagnosis[]
  function setReasonForVisit(a: Diagnosis[]) {
    reasonForVisit = a
  }

  const current = (active?: boolean): Worksheet => {
    let diagnoses: Diagnosis[]
    let procedures: Procedure[]

    const icdFn = isReview ? icds.actions.all : icds.actions.values
    const cptFn = isReview ? cpts.actions.all : cpts.actions.values

    diagnoses = icdFn().map((v, i) => diagnosis({ ...v, ...{ lineNumber: i + 1 } }, workflow))

    procedures = cptFn().map((v, i) => {
      const p = procedure({ ...v, ...{ lineNumber: i + 1 } } as any, workflow)
      setProcedureDiagnoses({ diagnoses }, p, {
        emLinks,
        cptLinks,
        activeOnly: isReview,
      })
      return p
    })

    setAllDiagnosesOnEmProcedures({
      diagnoses: diagnoses.filter((v) => v.active),
      procedures: active ? procedures.filter((v) => v.active) : procedures,
    })

    return { ...worksheet, ...{ diagnoses, procedures, reasonForVisit } }
  }

  function getPlaceOfService() {
    if (!hasPlace) {
      return null
    }
    //TODO: get default by department
    const value = placeOfService.value.getState().value || config?.placeOfService[0]
    return value
  }

  const newCpt = (code: CodeSet, values: CodeEntry) => {
    if (!code) {
      return null
    }

    const cpt = procedure(
      {
        ...code,
        ...editingCpt,
        ...{
          cptCode: code.code,
          description: code.description,
          provider: values.provider,
          secondaryProvider: values.secondary,
          tertiaryProvider: values.tertiary,
          serviceDate: values.serviceDate,
          links: values.links,
          modifier: values.modifier,
          multiplier: values.multiplier,
          ndcInformation: editingCpt?.ndcInformation || code.ndcInformation,
          serviceLocation: getPlaceOfService(),
        },
      },
      workflow,
    )

    const emResult = calculator.getState().result
    if (emResult) {
      const { calculatedEM, suggestedEM, noppEM } = emResult
      const n = parseInt(cpt.cptCode)

      if (n === calculatedEM || n === suggestedEM || n === noppEM) {
        cpt.emCalculator = emElements(emResult)
      }
    }

    if (bundle) {
      cpt.docBundleVersion = bundle.version
    }
    //  console.log('emElements cpt', cpt)
    return setProcedureDiagnoses(current(true), cpt, {
      emLinks,
      cptLinks,
      activeOnly: isReview,
    })
  }

  const { auditCPT, auditCPTMod } = context.config
  function setProviders(a: Procedure) {
    if (!a) {
      return
    }
    a.provider = providers.find((v) => v.providerId === a.provider?.providerId) || provider.value.getState().value
    a.secondaryProvider =
      providers.find((v) => v.providerId === a.secondaryProvider?.providerId) || secondary.value.getState().value
    a.tertiaryProvider =
      providers.find((v) => v.providerId === a.tertiaryProvider?.providerId) || tertiary.value.getState().value
  }

  function addNdcInfo(p: Procedure): Promise<Procedure> {
    return new Promise((resolve) => {
      if (p.promptNDC) {
        ndc.actions.getState().edit(p, (a: Procedure) => {
          resolve(a)
        })
      } else {
        resolve(p)
      }
    })
  }

  async function reviewEdit(update: Procedure, editingCpt: Procedure, onComplete?: () => void, onCancel?: () => void) {
    if (update.cptCode !== editingCpt?.cptCode) {
      const dif = await findDxOrProcedureRequestList({
        code: update.cptCode,
        serviceDate: utcDateString(update.serviceDate || editingCpt.serviceDate),
      })
      if (dif.success && Array.isArray(dif.result) && dif.result.length > 0) {
        const ndcCpt = await addNdcInfo(procedure(dif.result[0], workflow))
        update.ndcInformation = ndcCpt.ndcInformation
      }
    }

    editingCpt.modifiers = [].concat(editingCpt.modifiers || [])
    editingCpt.modifier = modifierLinkString(editingCpt)

    // console.log('available reason lists',  context.config)
    if (hasPlace) {
      editingCpt.serviceLocation = editingCpt.serviceLocation || null
    }

    reviewChangesOr(
      {
        title: 'Review Changes for CPT ' + update.cptCode,
        original: editingCpt,
        update,
        changeReasons: {
          modifier: auditCPTMod,
          modifiers: auditCPTMod,
          multiplier: auditCPT,
          links: auditCPT,
          diagnosisList: auditCPT,
          cptCode: auditCPT,
          serviceDate: auditCPT,
          serviceLocation: auditCPT,
          provider: auditCPT,
          secondaryProvider: auditCPT,
          tertiaryProvider: auditCPT,
          ndcInformation: auditCPT,
          emCalculator: update.emCalculator ? auditCPT : undefined,
        },
        fieldNames: [
          'cptCode',
          'provider',
          'serviceDate',
          'modifier',
          'modifiers',
          'multiplier',
          'links',
          'ndcInformation',
          'emCalculator',
          'serviceLocation',
          'provider',
          'secondaryProvider',
          'tertiaryProvider',
        ],
        onComplete: (a: Audit) => {
          updateElementAudit(update, a)
          if (editingCpt?.audit) {
            editingCpt.audit.notes = a.notes
          }

          updateElementAudit(editingCpt, a)
          // console.log('Review CPT Changes', editingCpt.audit);
          onComplete && onComplete()
        },
        onCancel: () => {
          clearEditCpt()
          if (onCancel) {
            onCancel()
          }
        },
      },
      () => onComplete && onComplete(),
    )
  }

  function addCpt(
    code: CodeSet | Procedure,
    add = true,
    review = false,
    override = null,
    complete: (cpt: Procedure) => void = null,
  ): Procedure {
    if (!code || typeof code === 'string') {
      return null
    }
    validateAccount()

    const { values } = form

    override && setProviders(override)

    const p: Procedure = code.cptCode
      ? (code as Procedure)
      : {
          ...newCpt(code as CodeSet, values.getState().value),
          ...override,
        }
    const isRequired = calculator.getState().required

    if (p.isEMCode && !editingCpt) {
      const existingEm = cpts.actions.values().find((v) => v.isEMCode)
      if (existingEm) {
        p.provider = existingEm.provider
        p.secondaryProvider = existingEm.secondaryProvider
        p.tertiaryProvider = existingEm.tertiaryProvider
      }

      if (isRequired && !p.emCalculator) {
        alert('EM Calculator is Required for EM Code Creation.')
        return null
      }
    }

    const ndcCpt = p

    function addNdcInfo() {
      return new Promise((resolve) => {
        if (p.promptNDC) {
          ndc.actions.getState().edit(ndcCpt, (a: Procedure) => {
            resolve(a)
          })
        } else {
          resolve(ndcCpt)
        }
      })
    }

    function onComplete() {
      if (ndcCpt) {
        if (editingCpt) {
          cpts.actions.replaceAt(ndcCpt, editingCpt)
        } else {
          const i = cpts.actions.add(ndcCpt)
          if (override) {
            cpts.actions.replaceAt(ndcCpt, i)
          }
        }
        if (complete) {
          complete(ndcCpt)
        }
      }
      editingCpt = null
      editingIcd = null
      cpts.data.setState({ edited: editingCpt })
    }

    if (add) {
      if (review) {
        addNdcInfo().then(() => {
          if (editingCpt) {
            reviewEdit(p, editingCpt, onComplete)
          } else {
            const title = 'Add Cpt ' + p.cptCode
            reviewActionOr(
              {
                title,
                list: auditCPT,
                existing: p?.audit,
                onComplete: (v) => {
                  v.action = editingCpt ? 'UPDATE' : 'CREATE'
                  p.audit = v
                  updateElementAudit(p, v)
                  onComplete()
                },
                onCancel: () => clearEditCpt(),
              },
              () => onComplete(),
            )
          }
        })
      } else {
        const i = cpts.actions.add(p)

        if (override) {
          cpts.actions.replaceAt(p, i)
        }
      }
      setChanged()
    }
    return p
  }

  function moveIcd(item: MoveItem) {
    icdMoved = icds.data.getState().values.slice()
    // console.log('move', icdMoved.map((a) => a.diagnosisCode).join(','));
    const el: Diagnosis = icds.data.getState().values[item.currentIndex]

    reviewActionOr(
      {
        title: 'Move ICD ',
        list: context.config.auditICD,
        onComplete: (a) => {
          a.action = 'MOVE'
          a.code = el?.diagnosisCode
          a.oldLineNumber = item.currentIndex + 1
          a.newLineNumber = item.newIndex + 1
          a.workflow = workflow

          worksheet.diagnosisMoves = worksheet.diagnosisMoves || []
          worksheet.diagnosisMoves.push(a)

          // TODO: review with Tom whether moves should be added to the same ICD or handled separatetly
          // updateElementAudit(el, a, true);

          worksheet.diagnosisMove = el.audit
          icds.actions.move(item)
        },
      },
      () => {
        icds.actions.move(item)
      },
    )
  }

  function moveCpt(item: MoveItem) {
    const el: Procedure = cpts.data.getState().values[item.currentIndex]
    reviewActionOr(
      {
        title: 'Move CPT ',
        list: auditCPT,
        onComplete: (a) => {
          a.action = 'MOVE'
          a.code = el?.cptCode
          a.oldLineNumber = item.currentIndex + 1
          a.newLineNumber = item.newIndex + 1
          a.workflow = workflow

          worksheet.procedureMoves = worksheet.procedureMoves || []
          worksheet.procedureMoves.push(a)

          // TODO: review with Tom whether moves should be added to the same CPT or handled separatetly
          // updateElementAudit(el, a, true);

          worksheet.procedureMove = el.audit

          cpts.actions.move(item)
          cpts.data.setState({ edited: null, editing: null })
        },
      },
      () => {
        cpts.actions.move(item)
        cpts.data.setState({ edited: null, editing: null })
      },
    )
  }

  function removeIcd(dx: Diagnosis) {
    function remove() {
      icds.actions.remove(dx)
      updateCptLinks()

      cpts.actions.refresh()
    }

    if (dx) {
      reviewActionOr(
        {
          title: 'Remove ICD ' + dx.diagnosisCode,
          list: context.config.auditICD,
          onComplete: (v) => {
            v.action = 'DELETE'
            updateElementAudit(dx, v)
            console.log('Owned by current user: ', createdByCurrentUser(dx), dx)
            remove()
          },
        },
        () => {
          remove()
        },
      )
    }
  }

  function removeCpt(cpt: Procedure): void {
    function remove() {
      cpts.actions.remove(cpt, hardDelete(cpt))
    }

    reviewActionOr(
      {
        title: 'Remove CPT ' + cpt.cptCode,
        list: auditCPT,
        onComplete: (v) => {
          v.action = 'DELETE'
          cpt.audit = v
          updateElementAudit(cpt, v)
          //TODO hard delete for worksheet / session owner
          // console.log('Owned by current user: ', createdByCurrentUser(cpt));
          remove()
        },
      },
      () => {
        remove()
      },
    )
  }
  let editCancel: () => void

  function clearEditCpt() {
    editingCpt = null
    cpts.data.setState({ edited: null, editing: null })
    form.setValues(defaultValues(), true)
    setTimeout(() => setCode('', true), 10)
    if (editCancel) {
      editCancel()
      editCancel = null
    }
  }

  let editingCpt: Procedure = null
  function editCpt(v: Procedure, setEdit = true, onCancel?: () => void) {
    editCancel = onCancel

    editingCpt = v
      if (setEdit) {
      cpts.data.setState({ edited: editingCpt })
      api.onEdit()

      cpts.actions.edit(v)
    }

    const providerId = v?.provider?.providerId
    const secId = v?.secondaryProvider?.providerId
    const terId = v?.tertiaryProvider?.providerId

    form.setValues({
      code: { ...v, ...{ code: v.cptCode } },
      modifier: modifierLinkString(v),
      multiplier: v.multiplier,
      links: v.links,
      serviceDate: v.serviceDate,
      serviceLocation:
        hasPlace && placeOfService.setListValue((u: { code: string }) => u.code === v.serviceLocation?.code),
      provider: provider.setListValue((u: Provider) => providerId === u?.providerId),
      secondary: secondary.setListValue((u: Provider) => secId === u?.providerId),
      tertiary: tertiary.setListValue((u: Provider) => terId === u?.providerId),
    })

    const child: HTMLInputElement = inputElement()
    if (child) {
      child.focus()
    }

    setEmCalculator(v)
  }

  let inputEl: HTMLInputElement
  function inputElement(): HTMLInputElement | null {
    if (inputEl) {
      return inputEl
    }
    const el: HTMLInputElement = code.inputRef.current as any
    if (el && el.children) {
      if (el.children[1] && el.children[1].children) {
        inputEl = el.children[1].children[0] as any
        return inputEl
      }
    }
    return null
  }

  function inputValue(c?: CodeSet, clearInput = false): string | null {
    const codeSet: CodeSet = c || code.value.getState().value
    if (codeSet?.code) {
      return codeSet?.code
    }
    if (input?.length) {
      return input
    }
    const el = inputElement()
    if (el?.value) {
      const value = el?.value
      if (clearInput) {
        el.value = ''
      }
      return value
    }
    return null
  }

  async function setCode(v: string | number, doSearch = true) {
    input = (v || '').toString()

    const child: HTMLInputElement = inputElement()
    if (child) {
      code.value.getState().setValue({ code: input })
      child.value = input
      child.focus()
      child.setSelectionRange(input.length, input.length)
      if (input?.length && doSearch) {
        await search(input)
      } else {
        if (code?.list) {
          code.list.getState().setValue([])
        }
      }
    }
  }

  const icdChanges = collectionChanges(icds, {
    changeLabel: (v: Diagnosis[]) => v.map((u) => u.diagnosisCode).join(', '),
  })

  const cptChanges = collectionChanges<Procedure>(cpts, {
    changeLabel: (v: Procedure[]) => v.map((u) => u.cptCode).join(', '),
  })

  function setEmCalculator(cpt: Procedure, setEm = true) {
    if (calculator) {
      calculator.getState().setProcedure(cpt)
      if (isReview && setEm) {
        editingCpt = cpt
      }
    }
  }

  function onEditEnd(cpt: Procedure) {
    console.info('onEditEnd', cpt)
    if (isReview) {
      editingCpt = null
    }
    if (worksheetReplacement) {
      validate()
    }
  }

  function resetTopForm() {
    form.setValues(defaultValues(), true)
    setTimeout(() => setCode('', true), 10)
  }
  const subscriptions = []
  subscriptions.push(
    calculator.subscribe(
      (disabled: boolean) => {
        form.disableControls(disabled)
      },
      (s) => s.readonly,
    ),
  )
  if (calculator) {
    let map: CptPatchMap
    loadCritCareCpts().then((set) => {
      map = cptPatchMap(set)
    })

    calculator.onResult = (v: EmResult) => {
      const critCareCodes = v?.criticalCareCodes || []
      // TODO check with Nick whether setting this is desirable
      // multiplier.value.setState({ value: v.multiplier || 1 });

      if (map) {
        const cpt = ({ code, multiplier }: CriticalCareCode) => {
          const obj = map[code]({
            ...form.values.getState().value,
            ...{
              multiplier: multiplier || 1,
            },
          })
          obj.cptCode = code
          return obj
        }

        const crit = critCareCodes.map(cpt)

        console.info('Critical Care Codes', critCareCodes, crit)

        const updated = updateCriticalCareV2(
          cpts.data.getState().all,
          crit,
          provider.value.getState().value,
          secondary.value.getState().value,
          tertiary.value.getState().value,
        )

        cpts.actions.setValuesDestructive(updated)
      } else {
        console.info('missing crit care cpts')
      }
    }

    if (worksheet?.procedures) {
      const em = worksheet.procedures.find((p) => p.emCalculator)
      if (em) {
        setTimeout(() => {
          setEmCalculator(em, false)
        }, 1500)
      }
    }
  }

  function setPhysician(providerId: String) {
    if (!providers) {
      return
    }
    const defaultProvider = providers.filter((v: Provider) => v.providerId === providerId)[0]

    if (defaultProvider) {
      initialValues.provider = defaultProvider
    }

    provider.setListValue((v: Provider) => v.providerId === providerId)
  }

  function cancelEdit() {
    form.setValues(defaultValues())
    editingCpt = null
    cpts.data.setState({ edited: null, editing: null })
    setCode(null)
  }

  subscriptions.push(() => icdCptKeyboardEvents())
  const destroy = () => subscriptions.forEach((fn) => fn())
  const inactiveIcds = inactiveListApi(icds)
  const inactiveCpts = inactiveListApi(cpts)

  const api = {
    worksheet,
    form,
    icds,
    inactiveIcds,
    icdChanges,
    cpts,
    inactiveCpts,
    billedCpts,
    cptChanges,
    notesApi,
    ndc,
    changed,
    isReview,
    worksheetReplacement,
    moveIcd,
    addIcd,
    removeIcd,
    editIcd,
    cancelEdit,
    removeCpt,
    addCpt,
    editCpt,
    moveCpt,
    setCode,
    setPhysician,
    setEmCalculator,
    onEditEnd,
    resetTopForm,
    findCode,
    current,
    validate,
    context,
    destroy,
    setChanged,
    setReasonForVisit,
    appendInactive,
    reviewEdit,
    setWorksheetReplacement,
    getValidated: () => validated,
    getFullText: () => fullText,
    onEdit: () => null,
    onFullTextChanged: (fn: (a: boolean) => void) => (fullTextChanged = fn),
  }

  icdCptKeyboardEvents(api)

  return api
}

export type RemoveIcdOptions = {
  dxToRemove: Diagnosis
  replacementDx: string
  dxList: Diagnosis[]
  cptList: Procedure[]
  removeCpt: boolean
  remove: () => void
  onRemove: (updated: [Diagnosis[], Procedure[]]) => void
}

export const removeIcdApi = create<RemoveIcdOptions>((set, get) => ({
  dxToRemove: null,
  replacementDx: null,
  dxList: null,
  cptList: null,
  removeCpt: true,
  onRemove: null,
  remove: () => {
    const { dxToRemove, replacementDx, dxList, cptList, removeCpt, onRemove } = get()
    if (dxToRemove) {
      const updated = removeDxFromCptList(dxToRemove, dxList, cptList, replacementDx, removeCpt)

      if (onRemove) {
        onRemove(updated)
      }
    }
    set({ dxToRemove: null, replacementDx: null })
  },
}))

let unsubscribe = null
function icdCptKeyboardEvents(api?: IcdCptApi) {
  if (unsubscribe) {
    unsubscribe()
  }
  unsubscribe = keyboardEvents((a: KeyboardApi) => {
    a.keydown.Escape(() => {
      api.cancelEdit()
    })
  }, api)
}
